import { IconAlertOctagonFilled, IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import dayjs from "dayjs";

import type { InboxListItem } from "~/server/domain/notifications";

import { mantineColorRamps, tokens } from "~/ui";

/**
 * Pure branching logic for `InboxMessageRow` (SC-3392 UAT fix): which body the shared frame renders,
 * whether the row's unread affordance shows, and whether it opens a detail modal. Extracted so it has
 * proof — `InboxMessageRow.tsx` is outside `src/ui`, so a `.tsx` rendering test there is not collected
 * by jest (`jest.config.ts:30`).
 */
export type InboxMessageRowKind = "task" | "notification";

type MessageTypeKind = InboxListItem["messageType"];

/** Structural, derived from `InboxListItem` rather than importing `@prisma/client` directly here. */
export type NotificationSeverityLevel = Extract<InboxListItem, { messageType: "NOTIFICATION" }>["severity"];

/** Structural, not `InboxListItem` itself, so a test fixture doesn't need a full hydrated Task. */
export const getInboxMessageRowKind = (item: { messageType: MessageTypeKind }): InboxMessageRowKind =>
  item.messageType === "TASK" ? "task" : "notification";

/** Unread affordance only applies to notifications — a TASK row's read state is never tracked (R1/R4). */
export const isInboxMessageUnread = (item: { messageType: MessageTypeKind; readAt?: Date | null }): boolean =>
  item.messageType === "NOTIFICATION" && !item.readAt;

/**
 * Only the NOTIFICATION body currently renders fuller detail through `onModalOpened` (round 12 UAT
 * fix); the TASK body provides none, so its row keeps today's click behavior and never opens a modal.
 */
export const inboxMessageRowKindHasModal = (kind: InboxMessageRowKind): boolean => kind === "notification";

/** Tabler icon component reference (not an instantiated element), same style `AssistantDebugModal.tsx`'s
 *  `const StatusIcon = ...` picks a component to render later with its own size/colour. */
type SeverityIconComponent = typeof IconAlertOctagonFilled;

/**
 * The severity icon shown in a NOTIFICATION row's/modal's avatar (SC-3392 round 16) — the same
 * danger/warning/info icon family `src/components/Alert.tsx`'s `DangerAlert`/`WarningAlert`/`InfoAlert`
 * already map to (`IconAlertOctagonFilled`, `IconAlertTriangle`, `IconInfoCircle`), so a Block-paired
 * (ERROR) notification reads with the same icon as any other danger surface in the app.
 */
export const getNotificationSeverityIcon = (severity: NotificationSeverityLevel): SeverityIconComponent => {
  if (severity === "ERROR") {
    return IconAlertOctagonFilled;
  }
  if (severity === "WARN") {
    return IconAlertTriangle;
  }
  return IconInfoCircle;
};

/**
 * The severity icon's colour (SC-3700 handoff §2): `~/ui` ramps, never a hex literal. INFO is the
 * `action.informational` token; ERROR and WARN take the ramp shade the severity avatar's table names
 * (danger[6], yellow[7]) — `content.danger` / `content.warning` are one shade off it.
 */
export const getNotificationSeverityIconColor = (severity: NotificationSeverityLevel): string => {
  if (severity === "ERROR") {
    return mantineColorRamps.danger[6];
  }
  if (severity === "WARN") {
    return mantineColorRamps.yellow[7];
  }
  return tokens.color.action.informational;
};

/** The severity avatar's circle background (SC-3700 handoff §2): ERROR danger[0], WARN yellow[1], INFO blue[1]. */
export const getNotificationSeverityIconSurface = (severity: NotificationSeverityLevel): string => {
  if (severity === "ERROR") {
    return mantineColorRamps.danger[0];
  }
  if (severity === "WARN") {
    return mantineColorRamps.yellow[1];
  }
  return mantineColorRamps.blue[1];
};

/** Severity is never conveyed by colour alone: the avatar's `title` / `aria-label` (handoff §2). */
export const getNotificationSeverityLabel = (severity: NotificationSeverityLevel): string => {
  if (severity === "ERROR") {
    return "Error";
  }
  if (severity === "WARN") {
    return "Warning";
  }
  return "Info";
};

/**
 * The compact age shown on a notification row (handoff §2): `25m` under an hour, `2h` under a day,
 * `3d` under a week, otherwise `MMM D`. A clock skewed into the future reads as `1m`, never `0m` / `-3m`.
 */
export const formatInboxRelativeTime = (createdAt: Date, now: Date): string => {
  const minutes = Math.max(1, dayjs(now).diff(createdAt, "minute"));
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = dayjs(now).diff(createdAt, "hour");
  if (hours < 24) {
    return `${hours}h`;
  }
  const days = dayjs(now).diff(createdAt, "day");
  if (days < 7) {
    return `${days}d`;
  }
  return dayjs(createdAt).format("MMM D");
};

interface NotificationDetailSectionsInput {
  message: string | null;
  context: string | null;
  detail: string | null;
}

/**
 * Which body the notification detail modal renders (SC-3392 round 2): "sections" when the producer
 * supplied a `context` and/or `detail` piece — a headline (title, rendered by the caller), a context
 * line and a detail line, each with different visual weight — or "fallback" to the single `message`
 * paragraph for a row (any past or future notification type) that never supplied the parts. Kept
 * generic — structural input, no source-specific shape — so a future notification type gets the same
 * sectioned treatment for free the moment it starts populating `context`/`detail`.
 */
export type NotificationDetailSections =
  { kind: "sections"; context: string | null; detail: string | null } | { kind: "fallback"; message: string | null };

export const getNotificationDetailSections = (item: NotificationDetailSectionsInput): NotificationDetailSections => {
  if (item.context !== null || item.detail !== null) {
    return { kind: "sections", context: item.context, detail: item.detail };
  }
  return { kind: "fallback", message: item.message };
};
